/**
 * Matching and dispatch — `/v1/admin/bookings/{id}/dispatch*`, `/v1/admin/dispatch/health`.
 *
 * **Every write here invalidates the booking reads too.** Sending a wave moves a booking from
 * `requested` to `matching`, and an assignment moves it to `accepted` — a dispatch board still
 * showing the old state after ops pressed the button is a board somebody will press again.
 *
 * **The replacement waterfall is strictly ordered** (doc 22 §5.5): promote a standing backup,
 * then re-dispatch, then give up. Each hook here is one step of it, and the screen offers them in
 * that order because each is slower and worse for the devotee than the one before.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type DispatchHistory = components['schemas']['DispatchHistory'];
export type BatchRow = components['schemas']['BatchRow'];
// The DISPATCH offer — a job offered to a pandit, not a promotion. Its schema is named for
// its table (`dispatch_offer`) because `offer` now also means a marketing campaign; the
// local alias keeps its old name so no screen had to change.
export type OfferRow = components['schemas']['DispatchOfferRow'];
export type ResponseRow = components['schemas']['ResponseRow'];
export type AssignmentRow = components['schemas']['AssignmentRow'];
export type CityDispatchHealth = components['schemas']['CityDispatchHealth'];
export type ReassignmentRow = components['schemas']['ReassignmentRow'];
type ReassignmentQueue = components['schemas']['ReassignmentQueue'];
type PromoteResponse = components['schemas']['PromoteResponse'];
type BookingRow = components['schemas']['BookingRow'];
type DispatchHealthResponse = components['schemas']['DispatchHealthResponse'];
type WaveResponse = components['schemas']['WaveResponse'];

export function useDispatchHistory(bookingId: number | null) {
  return useQuery({
    queryKey: keys.dispatch.history(bookingId ?? 0),
    queryFn: () =>
      authedClient.request<DispatchHistory>(`/v1/admin/bookings/${bookingId}/dispatch`),
    enabled: bookingId !== null,
  });
}

export function useDispatchHealth(windowDays: number) {
  return useQuery({
    queryKey: keys.dispatch.health(windowDays),
    queryFn: () =>
      authedClient.request<DispatchHealthResponse>(
        `/v1/admin/dispatch/health?window_days=${windowDays}`,
      ),
  });
}

/**
 * Every dispatch write invalidates every dispatch AND booking read.
 *
 * A hand-maintained map of which keys each write touches goes stale the first time somebody adds
 * a field — and the failure mode here is a board that shows a booking as unmatched after ops
 * matched it, which is exactly the state that gets a second wave sent by mistake.
 */
function useDispatchMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dispatch.all() });
      void queryClient.invalidateQueries({ queryKey: keys.bookings.all() });
    },
  });
}

/** Push the wave the sweep would have pushed later. */
export function useSendWave() {
  return useDispatchMutation(({ bookingId }: { bookingId: number }) =>
    authedClient.request<WaveResponse>(`/v1/admin/bookings/${bookingId}/dispatch`, {
      method: 'POST',
    }),
  );
}

/** A hand-picked wave. The server still runs every hard filter and refuses by name. */
export function useSendManualWave() {
  return useDispatchMutation(
    ({ bookingId, panditProfileIds }: { bookingId: number; panditProfileIds: number[] }) =>
      authedClient.request<WaveResponse>(`/v1/admin/bookings/${bookingId}/dispatch/manual`, {
        method: 'POST',
        body: { pandit_profile_ids: panditProfileIds },
      }),
  );
}

/** The concierge path: recording that a pandit agreed on the phone. Audited, and filter-checked. */
export function useAssignBooking() {
  return useDispatchMutation(
    ({
      bookingId,
      panditProfileId,
      reason,
    }: {
      bookingId: number;
      panditProfileId: number;
      reason: string;
    }) =>
      authedClient.request<AssignmentRow>(`/v1/admin/bookings/${bookingId}/assign`, {
        method: 'POST',
        body: { pandit_profile_id: panditProfileId, reason },
      }),
  );
}


// ── The replacement queue (docs/04-backend/22 §4.3) ─────────────────────────

export interface ReassignmentQuery extends Record<string, unknown> {
  city_id?: number;
  page: number;
  page_size: number;
}

export function reassignmentSearch(params: ReassignmentQuery): string {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('page_size', String(params.page_size));
  if (params.city_id !== undefined) search.set('city_id', String(params.city_id));
  return search.toString();
}

export function useReassignments(params: ReassignmentQuery) {
  return useQuery({
    queryKey: keys.dispatch.reassignments(params),
    queryFn: () =>
      authedClient.request<ReassignmentQueue>(
        `/v1/admin/reassignments?${reassignmentSearch(params)}`,
      ),
  });
}

/**
 * Step one of the waterfall, and the only one that ends well.
 *
 * Three outcomes, and each is a different next action: `promoted` is done; `no_standing_backup`
 * means the standby loop never got going; `backup_unavailable` means it did and failed at the
 * last moment. Both of the latter mean re-dispatch, but knowing which tells ops whether the loop
 * is working at all.
 */
export function usePromoteBackup() {
  return useDispatchMutation(({ bookingId }: { bookingId: number }) =>
    authedClient.request<PromoteResponse>(`/v1/admin/bookings/${bookingId}/promote-backup`, {
      method: 'POST',
    }),
  );
}

/** Step two: look again. Refused by name when there is no longer time, or after too many tries. */
export function useRedispatch() {
  return useDispatchMutation(({ bookingId }: { bookingId: number }) =>
    authedClient.request<WaveResponse>(`/v1/admin/bookings/${bookingId}/redispatch`, {
      method: 'POST',
    }),
  );
}

/** Step three, and the one nobody wants: give up, and start the refund. */
export function useReplacementFailed() {
  return useDispatchMutation(({ bookingId, reason }: { bookingId: number; reason: string }) =>
    authedClient.request<BookingRow>(`/v1/admin/bookings/${bookingId}/replacement-failed`, {
      method: 'POST',
      body: { reason },
    }),
  );
}

/** Taking work away from somebody for cause. Audited, and the reason is required. */
export function useQualityPull() {
  return useDispatchMutation(
    ({ assignmentId, reason, note }: { assignmentId: number; reason: string; note?: string }) =>
      authedClient.request<BookingRow>(`/v1/admin/assignments/${assignmentId}/quality-pull`, {
        method: 'POST',
        body: { reason, note },
      }),
  );
}

export type CandidateRoster = components['schemas']['RosterOut'];
export type RosterRow = components['schemas']['RosterRowOut'];
export type MatchExplain = components['schemas']['MatchExplainOut'];

/**
 * Every local pandit and the first filter that removed them (CSR-031).
 *
 * ⛔ **The aggregate, which no number of per-pandit calls can answer.** The question ops opens
 * this screen with is *"there are forty pandits in Patna, why did only two get offered?"* — and
 * to ask the per-pandit endpoint you must already know which pandit to ask about. The four
 * causes it separates are four different jobs for four different people.
 */
export function useCandidateRoster(bookingId: number | null) {
  return useQuery({
    queryKey: keys.dispatch.candidates(bookingId ?? 0),
    queryFn: () =>
      authedClient.request<CandidateRoster>(
        `/v1/admin/bookings/${bookingId}/dispatch/candidates`,
      ),
    enabled: bookingId !== null,
  });
}

/**
 * One pandit walked through every stage of the match (G10).
 *
 * Fetched only when somebody picks a row — the roster answers "who and why" for everybody, and
 * this answers "and what happened after that" for one, which is a question nobody asks about
 * forty people at once.
 */
export function useMatchExplain(bookingId: number | null, panditId: number | null) {
  return useQuery({
    queryKey: keys.dispatch.explain(bookingId ?? 0, panditId ?? 0),
    queryFn: () =>
      authedClient.request<MatchExplain>(
        `/v1/admin/bookings/${bookingId}/match-explain?pandit_id=${panditId}`,
      ),
    enabled: bookingId !== null && panditId !== null,
  });
}
